import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Grid,
  Avatar,
  Paper,
  Stack,
  IconButton,
  TextField,
  Button,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import RiderServices from "../services/externalServices/riderServices";

function BankDetails({ riderDetails,onDetailsUpdated  }) {
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState({
      ...riderDetails,
      bankDetails: riderDetails.bankDetails?.length
        ? riderDetails.bankDetails
        : [
            {
              bankName: "",
              accountHolderName: "",
              accountNumber: "",
              accountType: "",
              IFSC: "",
              UPIId: "",
              proofDocumentURL: "",
              isEnabled: false,
              isVerified: false,
              isDefault: false,
            },
          ],
    });
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
  
    const handleInputChange = (e, index) => {
      const { name, value } = e.target;
      setData((prev) => {
        const updatedBankDetails = [...prev.bankDetails];
        updatedBankDetails[index] = {
          ...updatedBankDetails[index],
          [name]: value,
        };
        return { ...prev, bankDetails: updatedBankDetails };
      });
    };
  
    const handleDropdownChange = (e, key, index) => {
      setData((prev) => {
        const updatedBankDetails = [...prev.bankDetails];
        updatedBankDetails[index] = {
          ...updatedBankDetails[index],
          [key]: e.target.value === "Yes",
        };
        return { ...prev, bankDetails: updatedBankDetails };
      });
    };
  
    const onSave = async (updatedData) => {
      setLoading(true);
      try {
        const response = await RiderServices.updateRider(
          riderDetails._id,
          updatedData
        );
        setLoading(false);
        setSuccessMessage("Bank details updated successfully!");
        onDetailsUpdated(updatedData);
      } catch (error) {
        console.error("Error updating bank details:", error);
        setLoading(false);
        setErrorMessage("Failed to update bank details. Please try again.");
      }
    };
  
    const handleSave = () => {
      setIsEditing(false);
      onSave(data);
    };
  
    return (
      <Card>
        <Stack direction="row" spacing={2} alignItems="center">
          <Paper
            elevation={3}
            style={{
              backgroundColor: "white",
              width: 50,
              height: 50,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: "3px",
            }}
          >
            <Avatar style={{ backgroundColor: "white" }} />
          </Paper>
          <CardHeader
            title={
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" color="#667085">
                  Bank Details
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setIsEditing(!isEditing)}
                  style={{ marginLeft: "auto" }}
                >
                  {isEditing ? <CloseIcon /> : <EditIcon />}
                </IconButton>
              </Stack>
            }
          />
        </Stack>
        <Divider />
        <CardContent>
          <Grid container spacing={2} marginTop={2}>
            {data.bankDetails.map((bank, index) => (
              <React.Fragment key={index}>
                {[
                  { label: "Bank Name", key: "bankName" },
                  { label: "Account Holder Name", key: "accountHolderName" },
                  { label: "Account Number", key: "accountNumber" },
                  { label: "Account Type", key: "accountType" },
                  { label: "IFSC Code", key: "IFSC" },
                  { label: "UPI ID", key: "UPIId" },
                ].map(({ label, key }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Typography variant="body2" color="#667085">
                      {label}:
                    </Typography>
                    {isEditing ? (
                      <TextField
                        name={key}
                        value={bank[key] || ""}
                        onChange={(e) => handleInputChange(e, index)}
                        fullWidth
                        size="small"
                      />
                    ) : (
                      <Typography variant="subtitle2">
                        {bank[key] || "N/A"}
                      </Typography>
                    )}
                  </Grid>
                ))}
  
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="#667085">
                    Proof Document URL:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      name="proofDocumentURL"
                      value={bank.proofDocumentURL || ""}
                      onChange={(e) => handleInputChange(e, index)}
                      fullWidth
                      size="small"
                    />
                  ) : (
                    <Typography variant="subtitle2">
                      {bank.proofDocumentURL || "N/A"}
                    </Typography>
                  )}
                </Grid>
  
                {[
                  { label: "Enabled", key: "isEnabled" },
                  { label: "Verified", key: "isVerified" },
                  { label: "Default", key: "isDefault" },
                ].map(({ label, key }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Typography variant="body2" color="#667085">
                      {label}:
                    </Typography>
                    {isEditing ? (
                      <FormControl fullWidth size="small">
                        <InputLabel id={`${key}-select-label`}>Select</InputLabel>
                        <Select
                          labelId={`${key}-select-label`}
                          value={bank[key] ? "Yes" : "No"}
                          onChange={(e) => handleDropdownChange(e, key, index)}
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="subtitle2">
                        {bank[key] ? "Yes" : "No"}
                      </Typography>
                    )}
                  </Grid>
                ))}
              </React.Fragment>
            ))}
          </Grid>
  
          {isEditing && (
            <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </Stack>
          )}
        </CardContent>
  
        <Snackbar
          open={Boolean(successMessage)}
          autoHideDuration={3000}
          onClose={() => setSuccessMessage("")}
        >
          <Alert
            onClose={() => setSuccessMessage("")}
            severity="success"
            sx={{ width: "100%" }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
  
        <Snackbar
          open={Boolean(errorMessage)}
          autoHideDuration={3000}
          onClose={() => setErrorMessage("")}
        >
          <Alert
            onClose={() => setErrorMessage("")}
            severity="error"
            sx={{ width: "100%" }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </Card>
    );
  }
  
  export default BankDetails;
  
  

